// HomesPulse — comprehensive property-listing form options (RESO-aligned).

export const PURPOSES = [
  { v: "for_sale", label: "For sale" },
  { v: "for_rent", label: "For rent" },
  { v: "for_lease", label: "For lease" },
  { v: "auction", label: "Auction" },
  { v: "new_construction", label: "New construction" },
  { v: "vacation_rental", label: "Vacation / short-term rental" },
  { v: "coming_soon", label: "Coming soon" },
];

export const CATEGORIES = [
  { v: "residential", label: "Residential" },
  { v: "residential_income", label: "Residential income / multifamily" },
  { v: "commercial", label: "Commercial" },
  { v: "land", label: "Land" },
  { v: "farm_ranch", label: "Farm / ranch" },
  { v: "manufactured", label: "Manufactured / mobile home" },
  { v: "business", label: "Business opportunity" },
];

export const SUBTYPES = [
  { v: "single_family", label: "Single-family residence", cats: ["residential"] },
  { v: "condominium", label: "Condominium", cats: ["residential"] },
  { v: "townhouse", label: "Townhouse", cats: ["residential"] },
  { v: "cooperative", label: "Cooperative", cats: ["residential"] },
  { v: "apartment", label: "Apartment", cats: ["residential", "residential_income"] },
  { v: "duplex", label: "Duplex", cats: ["residential_income"] },
  { v: "triplex", label: "Triplex", cats: ["residential_income"] },
  { v: "fourplex", label: "Fourplex", cats: ["residential_income"] },
  { v: "multifamily_building", label: "Multifamily building", cats: ["residential_income"] },
  { v: "manufactured_home", label: "Manufactured home", cats: ["manufactured"] },
  { v: "mobile_home", label: "Mobile home", cats: ["manufactured"] },
  { v: "residential_land", label: "Residential land", cats: ["land"] },
  { v: "commercial_land", label: "Commercial land", cats: ["land", "commercial"] },
  { v: "office", label: "Office", cats: ["commercial"] },
  { v: "retail", label: "Retail", cats: ["commercial"] },
  { v: "industrial", label: "Industrial", cats: ["commercial"] },
  { v: "warehouse", label: "Warehouse", cats: ["commercial"] },
  { v: "hospitality", label: "Hospitality", cats: ["commercial"] },
  { v: "mixed_use", label: "Mixed-use", cats: ["commercial"] },
  { v: "farm", label: "Farm / ranch land", cats: ["farm_ranch"] },
  { v: "other", label: "Other", cats: ["residential", "residential_income", "commercial", "land", "farm_ranch", "manufactured", "business"] },
];

export const LISTING_STATUSES = [
  { v: "draft", label: "Draft (private)" },
  { v: "coming_soon", label: "Coming soon" },
  { v: "active", label: "Active" },
  { v: "active_under_contract", label: "Active under contract" },
  { v: "pending", label: "Pending" },
  { v: "temporarily_off_market", label: "Temporarily off market" },
  { v: "sold", label: "Sold" },
  { v: "rented", label: "Rented" },
  { v: "withdrawn", label: "Withdrawn" },
  { v: "expired", label: "Expired" },
];

export const LISTING_SOURCES = [
  { v: "mls_idx", label: "MLS / IDX" },
  { v: "agent", label: "Listing agent" },
  { v: "brokerage", label: "Brokerage" },
  { v: "owner", label: "Property owner" },
  { v: "property_manager", label: "Property manager" },
  { v: "builder", label: "Builder / developer" },
  { v: "auction", label: "Auction company" },
  { v: "data_provider", label: "Licensed third-party data provider" },
];

export const OCCUPANCY = [
  { v: "owner_occupied", label: "Owner occupied" },
  { v: "tenant_occupied", label: "Tenant occupied" },
  { v: "vacant", label: "Vacant" },
  { v: "under_construction", label: "Under construction" },
];

export const POSSESSION = [
  { v: "at_closing", label: "At closing" },
  { v: "close_plus_days", label: "Close plus specified days" },
  { v: "negotiable", label: "Negotiable" },
  { v: "subject_to_tenant", label: "Subject to tenant rights" },
];

export const CONDITIONS = [
  "New construction", "Excellent", "Good", "Average", "Needs renovation", "Fixer-upper", "Tear-down",
];

export const OWNERSHIP_TYPES = [
  { v: "fee_simple", label: "Fee simple" },
  { v: "condominium", label: "Condominium" },
  { v: "cooperative", label: "Cooperative" },
  { v: "leasehold", label: "Leasehold" },
  { v: "other", label: "Other" },
];

export const LIVING_AREA_SOURCES = ["Appraiser", "Assessor", "Builder", "Owner", "Plans", "Other"];

export const FINANCING = ["Cash", "Conventional", "FHA", "VA", "USDA", "Owner financing", "Assumable loan", "Other"];
export const SALE_CONDITIONS = ["Standard", "Foreclosure", "Short sale", "Probate", "Estate sale", "Real-estate-owned", "Court approval required"];
export const HOA_FREQ = ["Monthly", "Quarterly", "Semi-annual", "Annual"];
export const FURNISHED = ["Furnished", "Unfurnished", "Partially furnished"];
export const LEASE_TYPES = ["Full service", "Gross", "Modified gross", "Net", "Double net", "Triple net"];
export const LEASE_RATE_UNITS = ["Per sq ft / year", "Per sq ft / month", "Monthly", "Annual"];

export const ADDRESS_PRIVACY = [
  { v: "exact", label: "Display exact street address" },
  { v: "approximate", label: "Display approximate map location only" },
  { v: "withheld", label: "Withhold address from the public" },
];

export const CONTACT_TYPES = [
  { v: "listing_agent", label: "Listing agent" },
  { v: "broker", label: "Broker" },
  { v: "owner", label: "Owner" },
  { v: "property_manager", label: "Property manager" },
  { v: "builder", label: "Builder representative" },
];

// ---- Feature checkbox groups (Interior / Exterior / Accessibility / Community) ----
export const INTERIOR_FEATURES = [
  "Hardwood floors", "Tile floors", "Carpet", "Fireplace", "Central heating", "Forced air",
  "Central A/C", "Ductless mini-split", "Tankless water heater", "Stainless appliances",
  "In-unit laundry", "Updated kitchen", "Kitchen island", "Walk-in closet", "Finished basement",
  "Attic storage", "Skylights", "Smart thermostat", "Security system", "Smart-home ready",
];
export const ACCESSIBILITY_FEATURES = [
  "Step-free entrance", "Accessible bathroom", "Accessible bedroom", "Wide doorways",
  "Elevator", "Stair lift", "Lowered fixtures",
];
export const EXTERIOR_FEATURES = [
  "Patio", "Porch", "Balcony", "Deck", "Fenced yard", "Private yard", "Professional landscaping",
  "Scenic view", "Waterfront", "Water access", "Private pool", "Community pool", "Spa / hot tub",
  "Outdoor kitchen", "EV charger", "Guest parking", "Additional structures",
];
export const COMMUNITY_FEATURES = [
  "Gated community", "Security patrol", "Clubhouse", "Fitness center", "Playground",
  "Walking trails", "Association approval required",
];

export const WATER_SOURCES = ["Public", "Well", "Shared well", "Other"];
export const SEWER = ["Public sewer", "Septic", "Other"];
export const SOLAR_OWNERSHIP = ["Owned", "Leased", "Power-purchase agreement"];

// ---- Fair Housing rule-based flagger ----
// Phrases/terms that commonly create Fair Housing / steering concerns.
export const FAIR_HOUSING_FLAGS = [
  { term: "perfect for young", why: "implies age / familial-status preference" },
  { term: "young couple", why: "implies familial status / age" },
  { term: "young professional", why: "implies age preference" },
  { term: "empty nester", why: "implies familial status / age" },
  { term: "no kids", why: "familial status discrimination" },
  { term: "no children", why: "familial status discrimination" },
  { term: "adults only", why: "familial status (unless legally exempt senior housing)" },
  { term: "singles", why: "may imply familial-status preference" },
  { term: "bachelor", why: "may imply sex / familial-status preference" },
  { term: "family-friendly", why: "familial-status steering" },
  { term: "ideal for families", why: "familial-status steering" },
  { term: "great for families", why: "familial-status steering" },
  { term: "christian", why: "religious preference" },
  { term: "catholic", why: "religious preference" },
  { term: "church", why: "may imply religious preference — describe as a landmark only if factual" },
  { term: "jewish", why: "religious / national-origin preference" },
  { term: "muslim", why: "religious preference" },
  { term: "safe neighborhood", why: "subjective safety claim can imply steering" },
  { term: "safe area", why: "subjective safety claim can imply steering" },
  { term: "exclusive neighborhood", why: "may imply exclusion of protected classes" },
  { term: "integrated", why: "references racial composition" },
  { term: "no section 8", why: "may violate source-of-income protections" },
  { term: "able-bodied", why: "disability discrimination" },
  { term: "handicap", why: "use neutral, factual accessibility language" },
  { term: "master bedroom", why: "consider the neutral term 'primary bedroom'" },
  { term: "walking distance", why: "consider 'near' — some buyers cannot walk (disability)" },
  { term: "english speaking", why: "national-origin discrimination" },
  { term: "mature", why: "may imply age preference" },
  { term: "retirees", why: "implies age (unless legally exempt senior housing)" },
];

export function scanFairHousing(text) {
  if (!text) return [];
  const lower = text.toLowerCase();
  return FAIR_HOUSING_FLAGS.filter((f) => lower.includes(f.term));
}

// Map the detailed subtype -> the searchable property_type vocabulary the backend indexes.
export function toPropertyType(category, subtype) {
  const s = subtype || "";
  if (["office", "retail", "industrial", "warehouse", "hospitality", "mixed_use"].includes(s)) return "commercial";
  if (["residential_land", "commercial_land", "farm"].includes(s)) return "land";
  if (category === "land" || category === "farm_ranch") return "land";
  if (["duplex", "triplex", "fourplex", "multifamily_building"].includes(s) || category === "residential_income") return "multifamily";
  if (["manufactured_home", "mobile_home"].includes(s) || category === "manufactured") return "mobile_home";
  if (s === "condominium") return "condo";
  if (s === "townhouse") return "townhouse";
  if (s === "cooperative") return "condo";
  if (s === "apartment") return "apartment";
  return "single_family";
}

// Map purpose -> the top-level searchable status (for_sale | for_rent).
export function toStatus(purpose) {
  return ["for_rent", "for_lease", "vacation_rental"].includes(purpose) ? "for_rent" : "for_sale";
}
